import { parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { enUS } from 'date-fns/locale';

export const TIMESTAMP_DISPLAY_FORMAT = "MMM. d, yyyy 'at' h:mm aaaa 'E.T'";
const TIMESTAMP_PARSE_FROM_CRM = 'M/d/yyyy h:mm:ss a';

export function formatConversationDate(dateString) {
  // Parse the input date string as UTC
  let utcDate;
  try {
    utcDate = parse(dateString, TIMESTAMP_PARSE_FROM_CRM, new Date(0));
    utcDate.setUTCFullYear(utcDate.getFullYear());
    utcDate.setUTCMonth(utcDate.getMonth());
    utcDate.setUTCDate(utcDate.getDate());
    utcDate.setUTCHours(utcDate.getHours());
    utcDate.setUTCMinutes(utcDate.getMinutes());
    utcDate.setUTCSeconds(utcDate.getSeconds());
  } catch (error) {
    // TODO: This catch block doesn't seem to be hit in testing. johall-tw
    // istanbul ignore next
    return 'Invalid Date';
  }

  // Ensure the date is valid
  if (Number.isNaN(utcDate.getTime())) {
    return 'Invalid Date';
  }

  // Format the UTC date in Eastern Time
  return formatInTimeZone(
    utcDate,
    'America/New_York',
    TIMESTAMP_DISPLAY_FORMAT,
    {
      locale: enUS,
    },
  ).replace(/[AaPp]\.{0,1}[Mm]\.{0,1}/, match => `${match.toLowerCase()}`);
}

/**
 * @typedef {{id: string, name: string}} Attachment
 */

/**
 * @typedef {Object} Correspondence
 * @property {string} id
 * @property {string} type
 * @property {Object} attributes
 * @property {string} attributes.messageType
 * @property {string} attributes.createdOn
 * @property {string} attributes.modifiedOn
 * @property {string} attributes.description
 * @property {Array<Attachment>|null} attributes.attachments
 */

/**
 * Builds a conversation array from inquiry attributes.
 * Filters out Notification correspondences, sorts by createdOn ascending,
 * formats dates, and prepends the submitter's question as the first item.
 *
 * @param {Object} attributes - The attributes object from an inquiry response
 * @param {string} attributes.createdOn - The date the inquiry was created
 * @param {string} attributes.submitterQuestion - The original question submitted
 * @param {Array<Attachment>|null} attributes.attachments - Attachments on the original inquiry
 * @param {Object} attributes.correspondences - The correspondences object
 * @param {Array<Correspondence>|null} attributes.correspondences.data - The list of correspondence items
 * @returns {Array} Conversation items, filtered and sorted with the submitter question first
 */
export const buildConversation = attributes => {
  const replies = (attributes.correspondences?.data || [])
    .filter(
      item =>
        item.attributes?.messageType !== 'Notification' &&
        item.attributes?.createdOn,
    )
    .sort((a, b) => {
      const dateA = parse(
        a.attributes.createdOn,
        TIMESTAMP_PARSE_FROM_CRM,
        new Date(),
      );
      const dateB = parse(
        b.attributes.createdOn,
        TIMESTAMP_PARSE_FROM_CRM,
        new Date(),
      );
      return dateA.getTime() - dateB.getTime();
    })
    .map(item => ({
      ...item,
      attributes: {
        ...item.attributes,
        createdOn: formatConversationDate(item.attributes.createdOn),
      },
    }));

  return [
    {
      id: 'submitterQuestion',
      type: 'submitterQuestion',
      attributes: {
        createdOn: formatConversationDate(attributes.createdOn),
        messageType: 'Your question',
        description: attributes.submitterQuestion,
        attachments: attributes.attachments,
      },
    },
    ...replies,
  ];
};
